import {createHash} from 'crypto-pro-csp-compatible';

import {EFileExtensions} from 'shared/constants/file-extensions';

import {fetchFileHash, fetchFileInfo} from '../api';
import {
    CADESCOM_HASH_ALGORITHM_CP_SHA1, DEFAULT_SIGNED_FILE_NAME, MAX_FILE_SIZE_TO_SIGN, EFileEncoding,
} from '../model/constants';
import {TCertificateExtended, TCryptoProSignatureType} from '../model/types';
import {concatenateBuffers, splitBuffer, stringToArrayBuffer} from './buffer';
import {getFormattedSignatureData} from './encoding';
import {getSignatureByType} from './get-sign-by-type';

interface ISignContentArgs {
    file?: File;
    docId?: string;
    attachId?: string;
    entityName?: string;
    certificateThumbprint: string;
    certificateCadescomAlgorithm: number;
    selectedCertificate: TCertificateExtended;
    selectedSignatureType: TCryptoProSignatureType;
    selectedFileExtension?: string;
    selectedFileEncoding?: EFileEncoding;
    signedFileName?: string;
    precomputedFileHash?: string;
}

interface ISignContentResult {
    signatureFile: File;
    fileName?: string;
    publishAllowed?: boolean;
}

/**
 * Подписывает файл или документа с использованием CryptoPro.
 * Поддерживает создание как присоединённую (attached), так и отсоединённую (detached) подписей.
 *
 * @param file Файл для подписания.
 * Если не указан, будет использоваться документ, идентифицируемый `docId` и `entityName`.
 * @param docId Идентификатор документа для подписания (используется, если файл не указан).
 * @param entityName Название сущности, связанной с документом для подписания (используется, если файл не указан).
 * @param certificateThumbprint Отпечаток сертификата для подписи.
 * @param certificateCadescomAlgorithm Алгоритм подписи Cadescom.
 * @param selectedCertificate Выбранный сертификат, расширенный дополнительной информацией.
 * @param selectedSignatureType Тип подписи (вложенная или отделённая).
 * @param selectedFileExtension Расширение для создаваемого файла подписи. По умолчанию — `.sig`.
 * @param signedFileName Название файла подписи по умолчанию, если имя не указано. По умолчанию — `sgn`.
 * @param attachId ID уже сохраненного в БД файла (например форма на странице уже созданного запроса).
 * @param precomputedFileHash заранее рассчитанный хэщ файла.
 *
 * @returns Объект `SignContentResult`, содержащий файл с подписью, имя файла и флаг разрешения публикации.
 *
 * @throws Если отсутствуют обязательные параметры `docId` и `entityName` при подписании документа.
 */
export const signContent = async ({
    file,
    docId,
    attachId,
    entityName,
    certificateThumbprint,
    certificateCadescomAlgorithm,
    selectedCertificate,
    selectedSignatureType,
    selectedFileExtension = EFileExtensions.SIG,
    selectedFileEncoding = EFileEncoding.BASE64,
    signedFileName = DEFAULT_SIGNED_FILE_NAME,
    precomputedFileHash,
}: ISignContentArgs): Promise<ISignContentResult> => {
    const getFileMetaToSign = async () => {
        if (!file) throw new Error('Файл не указан');

        const fileSize = file.size;
        const fileName = file.name || signedFileName;

        // Если файл меньше 25 MB, просто считаем хэш целиком
        if (fileSize <= MAX_FILE_SIZE_TO_SIGN) {
            const fileBuffer = await file.arrayBuffer();
            const fileHash = await createHash(fileBuffer, {hashedAlgorithm: certificateCadescomAlgorithm});
            return {fileHash, fileName, publishAllowed: false};
        }

        // Если файл больше 25 MB, разбиваем его на части, согласно рекомендациям из документации КриптоПро
        // Читаем весь файл и разбиваем на части
        const fileBuffer = await file.arrayBuffer();
        const chunks = splitBuffer(fileBuffer, MAX_FILE_SIZE_TO_SIGN);

        // Вычисляем хэш для каждой части
        const chunkHashes = await Promise.all(
            chunks.map(chunk => createHash(chunk, {hashedAlgorithm: certificateCadescomAlgorithm})),
        );

        // Конвертируем хэши из строк в ArrayBuffer[]
        const chunkHashBuffers = chunkHashes.map(hash => stringToArrayBuffer(hash));

        // Вычисляем общий хэш на основе хэшей частей
        const combinedHashBuffer = concatenateBuffers(chunkHashBuffers);
        const fileHash = await createHash(combinedHashBuffer, {hashedAlgorithm: certificateCadescomAlgorithm});

        return {fileHash, fileName, publishAllowed: false};
    };

    const getFileMetaToSignFromDoc = async () => {
        if (!entityName || (!docId && !attachId)) {
            throw new Error('Для подписания документа требуется docId либо attachId, а также entityName');
        }

        if (precomputedFileHash && attachId) {
            return {
                fileHash: precomputedFileHash,
                fileName: file?.name || signedFileName,
                publishAllowed: false,
            };
        }

        const fileHash = (await fetchFileHash({
            entityName,
            docId,
            attachId,
            algorithmCode: certificateCadescomAlgorithm,
        }));

        if (docId) {
            const {
                fileName = signedFileName,
                publishAllowed = false,
            } = await fetchFileInfo(entityName, docId);
            return {fileHash, fileName, publishAllowed};
        }

        return {fileHash, fileName: file?.name || signedFileName};
    };

    const {fileHash, fileName, publishAllowed} = file
        ? await getFileMetaToSign()
        : await getFileMetaToSignFromDoc();

    const privateKey = await selectedCertificate.certificate.getCadesProp('PrivateKey');
    if (certificateCadescomAlgorithm !== CADESCOM_HASH_ALGORITHM_CP_SHA1) {
        await privateKey.propset_CachePin(true);
    }

    const signature = await getSignatureByType({
        selectedSignatureType,
        certificateThumbprint,
        certificateCadescomAlgorithm,
        fileHash,
        entityName,
        docId: file ? undefined : docId,
        attachId,
        fileArrayBuffer: file ? await file.arrayBuffer() : undefined,
    });

    const signatureFileData = getFormattedSignatureData(signature, selectedFileEncoding);

    return {
        signatureFile: new File(
            [signatureFileData?.file as BlobPart],
            `${fileName}${selectedFileExtension}`,
            {type: signatureFileData?.type},
        ),
        fileName,
        publishAllowed,
    };
};
